/**
 * Who a folder, project or document has been shared with in the Customer Portal, and sharing it with one more contact.
 *
 * ONE DIALOG FOR THE THREE KINDS, because the server's model is one (cres_t1/service/customer_share.py). Authority is
 * the server's too: Manage on a folder, project:shareWithCustomer / document:shareWithCustomer on a record. The
 * screens that open this offer it only to somebody who holds that, and the server refuses everyone else regardless.
 *
 * WHAT A CONTAINER SHARE MEANS IS SAID BEFORE ANYBODY SHARES ONE: the contact sees the latest released revision of every
 * record in it, with its files, including records filed later. That is the whole consequence of the act and it is not
 * left to be discovered.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {
    Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControlLabel, MenuItem, TextField, Typography,
} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey5, Grey7, Grey17} from "../../theme/styles";

export type ShareKind = "Folder" | "Project" | "Document";

// A RECIPIENT IS A CUSTOMER PORTAL POSITION OR ONE CONTACT (2026-11-04). A position reaches whoever holds it when they
// read, so it is offered first; a contact is for the one-off.
type Recipient = {USID: string; Name: string; Email?: string; CompanyName?: string | null; Enabled?: boolean;
                  Locked?: boolean; HasSignedIn?: boolean; Kind: "Position" | "Contact"; Holders?: number};
type Level = {USID: string; Name: string; Description?: string | null; IsDefault: boolean};
type Share = {USID: string; Name: string; Email?: string | null; CompanyName?: string | null; Note?: string | null;
              RecipientKind?: "Position" | "Contact"; Holders?: number | null; Level?: string | null;
              SharedDatetime?: string | null; ExpiresDatetime?: string | null; RevokedDatetime?: string | null;
              Live: boolean; HasSignedIn: boolean; LastOpenedDatetime?: string | null};

const ROUTE = "t1/v1/customer_shares";

/** What sharing one of these gives the contact, in the words the dialog shows before anybody shares. */
export function consequence(kind: ShareKind): string {
    if (kind === "Document") {
        return "They will see this document's latest released revision, with its files. A draft is never shown; a new "
            + "revision reaches them when it is released.";
    }
    const container = kind === "Folder" ? "folder and the folders below it" : "project";
    return `They will see the latest released revision of every record in this ${container}, with its files, `
        + "including records added later. Drafts are never shown.";
}

function when(value?: string | null): string {
    if (!value) return "";
    const at = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(at.getTime()) ? String(value).slice(0, 10)
        : at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

export default function CustomerSharesDialog({kind, subjectUSID, subjectName, onClose}: {
    kind: ShareKind; subjectUSID: string; subjectName?: string | null; onClose: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [shares, setShares] = useState<Share[] | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [recipient, setRecipient] = useState<Recipient | null>(null);
    const [levels, setLevels] = useState<Level[]>([]);
    // "" IS THE CUSTOMER PORTAL ROLE'S LEVEL: the share carries none of its own and follows the role if it changes.
    const [level, setLevel] = useState("");
    const [note, setNote] = useState("");
    const [expires, setExpires] = useState("");
    const [neverExpires, setNeverExpires] = useState(false);
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const load = useCallback(() => {
        api.get(ROUTE, {params: {Kind: kind, SubjectUSID: subjectUSID}})
            .then((response: any) => setShares(response?.data?.Results ?? []))
            .catch((failure: any) => {
                setShares([]);
                setProblem(failure?.response?.data?.Cause ?? "Its customer shares could not be listed.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, subjectUSID]);

    useEffect(() => {
        load();
        api.get(ROUTE, {params: {Kind: kind, SubjectUSID: subjectUSID, View: "recipients"}})
            .then((response: any) => {
                const data = response?.data ?? {};
                setRecipients([...(data.Positions ?? []).map((one: any) => ({...one, Kind: "Position"})),
                               ...(data.Results ?? []).map((one: any) => ({...one, Kind: "Contact"}))]);
                setLevels(data.Levels ?? []);
            })
            .catch(() => setRecipients([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const share = async () => {
        if (!recipient) return;
        setSaving(true);
        setProblem(null);
        try {
            await api.post(ROUTE, {Kind: kind, SubjectUSID: subjectUSID,
                                   ...(recipient.Kind === "Position" ? {RecipientPositionUSID: recipient.USID} : {RecipientEmail: recipient.Email}),
                                   Level: level || undefined,
                                   Note: note.trim() || undefined, NeverExpires: neverExpires,
                                   ExpiresDatetime: neverExpires || expires === "" ? undefined : expires});
            setRecipient(null);
            setLevel("");
            setNote("");
            setExpires("");
            setNeverExpires(false);
            load();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? "That share could not be made.");
        }
        setSaving(false);
    };

    const revoke = async (one: Share) => {
        setProblem(null);
        try {
            await api.delete(ROUTE, {params: {USID: one.USID}});
            load();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? "That share could not be revoked.");
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Share {subjectName ? `“${subjectName}”` : `this ${kind.toLowerCase()}`} with a customer</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                    Customers read it in the Customer Portal. {consequence(kind)}
                </Typography>

                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17, mt: 2, mb: 1}}>Shared with</Typography>
                {shares === null ? <CircularProgress size={18}/> : shares.length === 0
                    ? <Typography sx={{fontSize: 13, color: Grey5}}>Nobody yet.</Typography>
                    : shares.map(one => (
                        <Box key={one.USID} sx={{display: "flex", alignItems: "center", gap: 1, py: 0.75}}>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography sx={{fontSize: 13.5, color: one.Live ? Grey17 : Grey5}}>
                                    {one.Name} <span style={{color: Grey7}}>
                                        {one.RecipientKind === "Position"
                                            ? `(position · ${one.Holders ?? 0} ${one.Holders === 1 ? "person holds" : "people hold"} it)`
                                            : `(${one.Email})`}
                                    </span>
                                </Typography>
                                <Typography sx={{fontSize: 12, color: Grey7}}>
                                    {one.Level ? `${one.Level} · ` : ""}
                                    {one.RevokedDatetime ? `Revoked ${when(one.RevokedDatetime)}`
                                        : !one.Live ? `Expired ${when(one.ExpiresDatetime)}`
                                        : `Shared ${when(one.SharedDatetime)}${one.ExpiresDatetime ? ` · until ${when(one.ExpiresDatetime)}` : " · no expiry"}`}
                                    {one.LastOpenedDatetime ? ` · last opened ${when(one.LastOpenedDatetime)}`
                                        : one.RecipientKind === "Position" ? " · not opened yet"
                                        : one.HasSignedIn ? " · not opened yet" : " · has never signed in"}
                                </Typography>
                            </Box>
                            {one.Live && <Button size="small" color="error" onClick={() => revoke(one)}>Revoke</Button>}
                        </Box>
                    ))}

                <Divider sx={{my: 2}}/>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17}}>Share with a customer</Typography>
                <Autocomplete
                    options={recipients}
                    value={recipient}
                    onChange={(_event, value) => setRecipient(value)}
                    groupBy={one => one.Kind === "Position" ? "Customer Portal positions" : "Individual contacts"}
                    getOptionLabel={one => one.Kind === "Position" ? one.Name : `${one.Name ?? ""} (${one.Email ?? ""})`}
                    isOptionEqualToValue={(one, other) => one.USID === other.USID && one.Kind === other.Kind}
                    renderOption={(props, one) => (
                        <Box component="li" {...props} key={`${one.Kind}:${one.USID}`} sx={{display: "block !important"}}>
                            <Typography sx={{fontSize: 13.5, color: Grey17}}>{one.Name}</Typography>
                            <Typography sx={{fontSize: 12, color: Grey7}}>
                                {one.Kind === "Position"
                                    ? `${one.Holders ?? 0} ${one.Holders === 1 ? "person holds" : "people hold"} this position; whoever holds it later sees it too`
                                    : <>
                                        {one.Email}{one.CompanyName ? ` · ${one.CompanyName}` : ""}
                                        {one.Enabled === false ? " · disabled" : ""}{one.Locked ? " · locked out" : ""}
                                        {one.HasSignedIn === false ? " · has never signed in" : ""}
                                    </>}
                            </Typography>
                        </Box>
                    )}
                    noOptionsText="No Customer Portal positions or contacts on this account. They are added in Administration."
                    renderInput={params => <TextField {...params} label="Customer Portal position or contact" size="small" margin="dense"/>}/>
                {/* WHAT THE SHARE LETS THEM DO. The default follows the Customer Portal role, which an administrator sets. */}
                <TextField select label="What they may do" size="small" margin="dense" fullWidth value={level}
                           onChange={event => setLevel(event.target.value)}
                           slotProps={{select: {displayEmpty: true}, inputLabel: {shrink: true}}}
                           helperText={(levels.find(one => one.USID === level)?.Description) ?? "Whatever the Customer Portal role allows."}>
                    <MenuItem value="">Default{levels.find(one => one.IsDefault) ? ` (${levels.find(one => one.IsDefault)!.Name})` : ""}</MenuItem>
                    {levels.map(one => <MenuItem key={one.USID} value={one.USID}>{one.Name}</MenuItem>)}
                </TextField>
                <TextField label="Note for them (optional)" size="small" margin="dense" fullWidth value={note}
                           onChange={event => setNote(event.target.value)}/>
                <Box sx={{display: "flex", alignItems: "center", gap: 2, mt: 1}}>
                    <TextField label="Expires" type="date" size="small" value={expires} disabled={neverExpires}
                               slotProps={{inputLabel: {shrink: true}}} helperText={neverExpires ? " " : "Leave empty for a year"}
                               onChange={event => setExpires(event.target.value)}/>
                    <FormControlLabel control={<Checkbox size="small" checked={neverExpires}
                                                         onChange={event => setNeverExpires(event.target.checked)}/>}
                                      label={<Typography sx={{fontSize: 13}}>Never expires</Typography>}/>
                </Box>
                {problem && <Typography sx={{fontSize: 13, color: "#B3261E", mt: 1}}>{problem}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" disabled={!recipient || saving} onClick={share}>
                    {saving ? "Sharing…" : "Share"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
